import { useRef, useState } from "react";
import { Upload, X, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR } from "@/lib/api";

/**
 * Two-phase catalogue import: pick a file → review what will happen → apply.
 * Duplicate rows (matched by SKU or barcode) default to "Update" (e.g. a price
 * list refresh) but the admin can switch any row to "Ignore" before applying.
 * Every new row is guaranteed a unique SKU (auto IMP-xxxx when missing).
 */
export function ImportReviewModal({ category = null, onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);

  const pick = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (category) fd.append("category", category);
      fd.append("mode", "preview");
      const { data } = await api.post("/admin/products/import-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const d = {};
      data.rows.forEach((r) => {
        if (r.status === "duplicate") d[String(r.i)] = "update";
      });
      setDecisions(d);
    } catch (err) {
      toast.error(errMsg(err));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const setAll = (v) => {
    const d = { ...decisions };
    preview.rows.forEach((r) => {
      // barcode matches are never ignored — they are the scan-and-reprice path
      if (r.status === "duplicate" && r.match !== "barcode") d[String(r.i)] = v;
    });
    setDecisions(d);
  };

  const apply = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (category) fd.append("category", category);
      fd.append("decisions", JSON.stringify(decisions));
      const { data } = await api.post("/admin/products/import-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Import done: ${data.created} added, ${data.updated} updated, ${data.skipped} skipped` +
          (data.errors.length ? `, ${data.errors.length} row errors` : "")
      );
      if (data.errors.length) console.warn("import errors:", data.errors);
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const dupRows = preview?.rows.filter((r) => r.status === "duplicate") || [];
  const errRows = preview?.rows.filter((r) => r.status === "error") || [];
  // barcode matches always update; SKU/in-file matches follow the admin's choice
  const isUpdate = (r) => r.match === "barcode" || decisions[String(r.i)] === "update";
  const updateCount = dupRows.filter(isUpdate).length;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-navy-dark/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl p-8 w-full max-w-2xl my-10" data-testid="import-review-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="font-display font-bold text-navy text-lg">Import product list</h3>
            {category && <p className="text-xs text-slate-400 mt-0.5">into category: <span className="font-semibold text-navy">{category}</span></p>}
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-navy" data-testid="import-review-cancel" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {!preview && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 p-6 text-center">
            <Upload size={20} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm font-semibold text-navy">Upload a file — CSV, Excel, PDF or Word</p>
            <p className="text-[11px] text-slate-400 mt-1">
              Header row with columns like: name/description, price, sku or barcode, stock.
              Items already in the catalogue will be shown for review first — nothing changes until you confirm.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls,.pdf,.docx"
              className="hidden"
              onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }}
              data-testid="import-review-file-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="mt-3 h-11 px-6 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm disabled:opacity-50 transition-colors"
              data-testid="import-review-choose-btn"
            >
              {busy ? "Reading file…" : "Choose file"}
            </button>
          </div>
        )}

        {preview && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
              <FileSpreadsheet size={14} className="text-slate-400" />
              <span className="font-semibold text-navy">{file?.name}</span> · {preview.total_rows} rows
              <button onClick={() => { setPreview(null); setFile(null); }} className="ml-auto font-semibold text-brand hover:underline" data-testid="import-review-back">
                Choose another file
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 text-center" data-testid="import-count-new">
                <p className="font-display text-2xl font-extrabold text-emerald-600">{preview.new}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">New items</p>
              </div>
              <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3 text-center" data-testid="import-count-duplicates">
                <p className="font-display text-2xl font-extrabold text-amber-600">{preview.duplicates}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Already in catalogue</p>
              </div>
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 text-center" data-testid="import-count-errors">
                <p className="font-display text-2xl font-extrabold text-slate-500">{preview.errors}</p>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Row errors</p>
              </div>
            </div>

            {dupRows.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-navy flex items-center gap-1.5">
                    <AlertTriangle size={14} className="text-amber-500" />
                    These already exist — barcode matches always update pricing; for SKU matches choose Update or Ignore
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => setAll("update")} className="text-[11px] font-bold text-brand hover:underline" data-testid="import-all-update">Update all</button>
                    <button onClick={() => setAll("ignore")} className="text-[11px] font-bold text-slate-500 hover:underline" data-testid="import-all-ignore">Ignore all</button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="import-duplicates-list">
                  {dupRows.map((r) => (
                    <div key={r.i} className="flex items-center gap-3 px-4 py-2.5" data-testid={`import-dup-row-${r.i}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-navy truncate">{r.name}</p>
                        <p className="text-[11px] text-slate-400 font-mono">
                          {r.sku}
                          {r.match === "file" ? " · also in this file" : r.existing ? ` · matches ${r.existing.sku} by ${r.match === "barcode" ? "barcode" : "SKU"}` : ""}
                        </p>
                        {r.existing && (
                          <p className="text-[11px] text-slate-500 mt-0.5">
                            Price {formatR(r.existing.price)} → <span className="font-bold text-brand">{formatR(r.price)}</span>
                            {" · "}Stock {r.existing.stock} → <span className="font-bold text-brand">{r.stock}</span>
                          </p>
                        )}
                      </div>
                      {r.match === "barcode" ? (
                        <span
                          className="shrink-0 h-7 px-3 rounded-full bg-brand/10 text-brand text-[11px] font-bold inline-flex items-center"
                          title="Matched by barcode — barcode rows always update pricing"
                          data-testid={`import-dup-barcode-${r.i}`}
                        >
                          Barcode — will update
                        </span>
                      ) : (
                      <div className="flex rounded-full bg-slate-100 p-0.5 shrink-0">
                        <button
                          onClick={() => setDecisions({ ...decisions, [String(r.i)]: "update" })}
                          className={`h-7 px-3 rounded-full text-[11px] font-bold transition-colors ${decisions[String(r.i)] === "update" ? "bg-navy text-white" : "text-slate-500"}`}
                          data-testid={`import-dup-update-${r.i}`}
                        >
                          Update
                        </button>
                        <button
                          onClick={() => setDecisions({ ...decisions, [String(r.i)]: "ignore" })}
                          className={`h-7 px-3 rounded-full text-[11px] font-bold transition-colors ${decisions[String(r.i)] === "ignore" ? "bg-slate-500 text-white" : "text-slate-500"}`}
                          data-testid={`import-dup-ignore-${r.i}`}
                        >
                          Ignore
                        </button>
                      </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {errRows.length > 0 && (
              <p className="text-xs text-red-500 mb-4" data-testid="import-errors-note">
                {errRows.length} row(s) will be skipped (e.g. missing name or invalid price).
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={apply}
                disabled={busy || (preview.new === 0 && updateCount === 0)}
                className="h-11 px-6 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm disabled:opacity-50 transition-colors"
                data-testid="import-apply-btn"
              >
                {busy
                  ? "Importing…"
                  : `Import ${preview.new} new${updateCount ? ` · update ${updateCount}` : ""}${dupRows.length - updateCount ? ` · skip ${dupRows.length - updateCount}` : ""}`}
              </button>
              <button onClick={onClose} className="h-11 px-6 rounded-full border border-slate-200 font-semibold text-sm" data-testid="import-review-cancel-2">
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
